import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import VpnKeyOutlined from "@mui/icons-material/VpnKeyOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { EmptyState, PageHeader, PageSkeleton } from "../components";
import CredentialPayloadForm from "../components/CredentialPayloadForm";
import CustomSnackbar, {
  CustomSnackbarRef,
} from "../components/CustomSnackbar";
import { useMedia } from "../context/MediaContext";
import { useProviders } from "../hooks/useProviders";
import { useMethods } from "../hooks/useMethods";
import {
  createProviderCredential,
  CredentialAccountSpec,
  CredentialScope,
  deleteProviderCredential,
  getProviderCredentialFields,
  getProviderCredentials,
  ProviderCredential,
  ProviderCredentialSchema,
  updateProviderCredential,
} from "../utils/api";

const OPERATION_TYPES = [
  "deposit",
  "payout",
  "withdrawal",
  "cashin",
  "transfer",
];

const errorText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail[0]?.msg ?? fallback;
  return detail.message ?? fallback;
};

type FormState = {
  provider_id: string;
  label: string;
  /** Прежний ввод «ИМЯ = значение» — для провайдеров, чьи поля не описаны. */
  payload: string;
  /** Выбранный счёт провайдера: под него и заводятся ключи. */
  account: string;
  /** Значения описанных полей: имя поля -> введённое значение. */
  values: Record<string, string>;
  payment_method: string;
  operation_type: string;
  currency: string;
  bank_name: string;
  minsum: string;
  maxsum: string;
  enabled: boolean;
};

const EMPTY_FORM: FormState = {
  provider_id: "",
  label: "",
  payload: "",
  account: "",
  values: {},
  payment_method: "",
  operation_type: "",
  currency: "",
  bank_name: "",
  minsum: "",
  maxsum: "",
  enabled: true,
};

/** Ключи вводятся построчно: «ИМЯ_ПОЛЯ = значение». */
const parsePayload = (text: string): Record<string, string> => {
  const result: Record<string, string> = {};
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const separator = line.indexOf("=");
      if (separator === -1) return;
      const key = line.slice(0, separator).trim();
      const value = line.slice(separator + 1).trim();
      if (key && value) result[key] = value;
    });
  return result;
};

/**
 * Область в виде строки — для сравнения наборов между собой.
 *
 * Ничью бэкенд не разрывает: два набора с одинаковой узостью у одного
 * провайдера — ошибка настройки, и побеждает тот, у кого меньше id, молча.
 * Поэтому второй такой набор не даём завести здесь.
 */
const scopeKey = (scope: CredentialScope) =>
  [
    scope.payment_method ?? "",
    scope.operation_type ?? "",
    scope.currency ?? "",
    (scope.bank_name ?? "").trim().toLowerCase(),
    scope.minsum ?? "",
    scope.maxsum ?? "",
  ].join("|");

const scopeSummary = (scope: CredentialScope) => {
  const parts: string[] = [];
  if (scope.payment_method) parts.push(scope.payment_method);
  if (scope.operation_type) parts.push(scope.operation_type);
  if (scope.currency) parts.push(scope.currency.toUpperCase());
  if (scope.bank_name) parts.push(scope.bank_name);
  if (scope.minsum != null || scope.maxsum != null) {
    parts.push(`${scope.minsum ?? "…"}–${scope.maxsum ?? "…"}`);
  }
  return parts;
};

const ProviderCredentialsPage = () => {
  const theme = useTheme();
  const { isMobile600 } = useMedia();
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  const { providers } = useProviders();
  const { methods, currency } = useMethods();

  const [items, setItems] = useState<ProviderCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [providerFilter, setProviderFilter] = useState<string>("");
  /** Схемы полей по провайдерам: по ним и строится форма ключей. */
  const [schemas, setSchemas] = useState<
    Record<number, ProviderCredentialSchema>
  >({});
  const [schemasLoading, setSchemasLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderCredential | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] =
    useState<ProviderCredential | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProviderCredentials(
        providerFilter ? Number(providerFilter) : undefined,
      );
      setItems(res.data?.items ?? []);
      setFailed(false);
    } catch (err) {
      setFailed(true);
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось загрузить наборы"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [providerFilter]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Схемы читаем разом на все провайдеры: их немного, а запрашивать по одной
   * при каждом выборе провайдера в форме — лишние круги на том же ответе.
   */
  useEffect(() => {
    let cancelled = false;
    getProviderCredentialFields()
      .then((res) => {
        if (cancelled) return;
        const items: ProviderCredentialSchema[] = res.data?.items ?? [];
        setSchemas(
          Object.fromEntries(items.map((item) => [item.provider_id, item])),
        );
      })
      .catch(() => {
        // Схем нет — на бэкенде без этой ручки форма останется прежней,
        // с ручным вводом имён.
      })
      .finally(() => {
        if (!cancelled) setSchemasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const schema = form.provider_id
    ? schemas[Number(form.provider_id)]
    : undefined;

  /** Счёт, под который заводятся ключи. Единственный выбирается сам. */
  const account: CredentialAccountSpec | undefined = schema
    ? (schema.accounts.find((item) => item.code === form.account) ??
      (schema.accounts.length === 1 ? schema.accounts[0] : undefined))
    : undefined;

  /**
   * Метод из области набора может не оказаться в справочнике — его подставил
   * счёт провайдера или он остался от прежней настройки. Без него в списке
   * поле выглядело бы пустым, а в наборе значение бы осталось.
   */
  const methodOptions = (() => {
    const list = Object.values(methods) as string[];
    return form.payment_method && !list.includes(form.payment_method)
      ? [form.payment_method, ...list]
      : list;
  })();

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, provider_id: providerFilter });
    setFormError(null);
    setFormOpen(true);
  };

  /**
   * Счёт выбирается областью набора, поэтому при его выборе область и
   * заполняем: иначе ключи бахрейнского счёта ушли бы в набор без метода и
   * применились к любому заказу.
   */
  const chooseAccount = (code: string) => {
    const picked = schema?.accounts.find((item) => item.code === code);
    setForm((prev) => ({
      ...prev,
      account: code,
      // Значения прежнего счёта не переносим: у него другие имена полей.
      values: {},
      payment_method:
        picked?.scope.payment_methods.length === 1
          ? picked.scope.payment_methods[0]
          : prev.payment_method,
      operation_type: picked?.scope.operation_type ?? prev.operation_type,
      minsum:
        picked?.scope.amount_from != null
          ? String(picked.scope.amount_from)
          : prev.minsum,
      maxsum:
        picked?.scope.amount_to != null
          ? String(picked.scope.amount_to)
          : prev.maxsum,
    }));
  };

  /**
   * Значения ключей не отдаются никогда, поэтому при правке поле набора всегда
   * пустое: пустым его и оставляют, если менять ключи не нужно.
   */
  const openEdit = (item: ProviderCredential) => {
    setEditing(item);
    // Какой счёт у набора, бэкенд не говорит — но имена полей он отдаёт, и
    // по ним счёт узнаётся. Иначе при правке пришлось бы выбирать его заново
    // наугад, а ошибка в счёте тихая.
    const known = schemas[item.provider_id];
    const matched = known?.accounts.find((candidate) =>
      candidate.fields.every((field) =>
        (item.fields ?? []).includes(field.name),
      ),
    );

    setForm({
      provider_id: String(item.provider_id),
      label: item.label,
      payload: "",
      account: matched?.code ?? "",
      values: {},
      payment_method: item.scope.payment_method ?? "",
      operation_type: item.scope.operation_type ?? "",
      currency: item.scope.currency ?? "",
      bank_name: item.scope.bank_name ?? "",
      minsum: item.scope.minsum != null ? String(item.scope.minsum) : "",
      maxsum: item.scope.maxsum != null ? String(item.scope.maxsum) : "",
      enabled: item.enabled,
    });
    setFormError(null);
    setFormOpen(true);
  };

  const buildScope = (): CredentialScope => ({
    payment_method: form.payment_method || null,
    operation_type: form.operation_type || null,
    currency: form.currency || null,
    bank_name: form.bank_name.trim() || null,
    minsum: form.minsum === "" ? null : Number(form.minsum),
    maxsum: form.maxsum === "" ? null : Number(form.maxsum),
  });

  const submit = async () => {
    if (!form.label.trim()) {
      setFormError("Заполните название набора");
      return;
    }
    if (!editing && !form.provider_id) {
      setFormError("Выберите провайдера");
      return;
    }

    if (schema && !schema.reads_sets) {
      setFormError(
        schema.note ??
          "Эта интеграция наборы не читает — заводить его бессмысленно",
      );
      return;
    }

    // Поля провайдера описаны — значения собираем по схеме, а не разбором
    // строк: именно в ручном имени и возникает тихая ошибка.
    const bySchema = Boolean(schema?.accounts.length);
    let payload: Record<string, string>;

    if (bySchema) {
      if (!account) {
        setFormError("Выберите счёт провайдера");
        return;
      }
      const described = [...(schema?.common_fields ?? []), ...account.fields];
      const filled = described
        .map((field) => [field.name, (form.values[field.name] ?? "").trim()])
        .filter(([, value]) => value !== "");
      payload = Object.fromEntries(filled);

      // Набор применяется целиком: ключ без парного секрета хуже, чем
      // ненастроенный набор — интеграция молча возьмёт пару из окружения.
      // При правке пустая форма означает «ключи не трогаем».
      const missing = account.fields.filter(
        (field) => field.required && !(form.values[field.name] ?? "").trim(),
      );
      if (missing.length && (!editing || filled.length > 0)) {
        setFormError(
          `Заполните все поля счёта: ${missing
            .map((field) => field.title)
            .join(", ")}. Набор применяется целиком.`,
        );
        return;
      }
    } else {
      payload = parsePayload(form.payload);
      if (!editing && Object.keys(payload).length === 0) {
        setFormError("Добавьте хотя бы одно поле набора");
        return;
      }
    }

    if (form.minsum !== "" && form.maxsum !== "") {
      if (Number(form.minsum) >= Number(form.maxsum)) {
        setFormError("Нижняя граница должна быть строго меньше верхней");
        return;
      }
    }

    // Ничью бэкенд не разрывает — берёт набор с меньшим id и пишет в лог.
    // Снаружи это выглядит как «настройки не применились», поэтому второй
    // набор с той же областью не выпускаем с формы.
    const providerId = editing ? editing.provider_id : Number(form.provider_id);
    const sameScope =
      // Список отфильтрован по провайдеру — чужие наборы в него не попали,
      // и сверять не с чем.
      (!providerFilter || providerFilter === String(providerId)) &&
      items.find(
        (row) =>
          row.provider_id === providerId &&
          row.id !== editing?.id &&
          scopeKey(row.scope) === scopeKey(buildScope()),
      );
    if (sameScope) {
      setFormError(
        `Набор «${sameScope.label}» уже закрывает ровно эту область. Два набора с одинаковой узостью спорят за заказ, и побеждает заведённый раньше — сузьте область.`,
      );
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await updateProviderCredential(editing.id, {
          label: form.label.trim(),
          // Пустое поле означает «ключи не трогаем»: payload заменяет набор
          // целиком, и отправка пустого объекта стёрла бы его.
          ...(Object.keys(payload).length ? { payload } : {}),
          scope: buildScope(),
          enabled: form.enabled,
        });
        snackbarRef.current?.showSnackbar("Набор обновлён", "success");
      } else {
        await createProviderCredential({
          provider_id: Number(form.provider_id),
          label: form.label.trim(),
          payload,
          scope: buildScope(),
          enabled: form.enabled,
        });
        snackbarRef.current?.showSnackbar("Набор создан", "success");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      setFormError(errorText(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (item: ProviderCredential) => {
    try {
      await updateProviderCredential(item.id, { enabled: !item.enabled });
      setItems((rows) =>
        rows.map((row) =>
          row.id === item.id ? { ...row, enabled: !row.enabled } : row,
        ),
      );
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось переключить"),
        "error",
      );
    }
  };

  const runDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteProviderCredential(confirmDelete.id);
      snackbarRef.current?.showSnackbar("Набор удалён", "success");
      setConfirmDelete(null);
      load();
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось удалить"),
        "error",
      );
    }
  };

  const fieldsCell = (item: ProviderCredential) => {
    if (!item.readable) {
      return (
        <Tooltip title="Набор зашифрован другим ключом или повреждён. Интеграция молча возьмёт переменные окружения — снаружи это выглядит как «настройки не применились»">
          <Chip
            size="small"
            icon={<WarningAmberRounded />}
            label="не читается"
            color="error"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Tooltip>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {(item.fields ?? []).map((field) => (
          <Chip
            key={field}
            size="small"
            label={field}
            sx={{ height: 20, fontSize: 11, fontFamily: "monospace" }}
          />
        ))}
      </Stack>
    );
  };

  const renderContent = () => {
    if (loading) return <PageSkeleton />;

    if (failed) {
      return (
        <EmptyState
          icon={VpnKeyOutlined}
          title="Наборы недоступны"
          description="Не удалось получить данные. Раздел доступен только администратору."
          action={
            <Button variant="contained" onClick={load}>
              Повторить
            </Button>
          }
        />
      );
    }

    if (!items.length) {
      return (
        <EmptyState
          icon={VpnKeyOutlined}
          title="Наборов нет"
          description="Заведите набор ключей, чтобы интеграция брала их вместо переменных окружения."
          action={
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={openCreate}
            >
              Добавить
            </Button>
          }
        />
      );
    }

    if (isMobile600) {
      return (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{ p: 2, borderRadius: "16px" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box minWidth={0}>
                  <Typography sx={{ fontWeight: 700 }}>{item.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.provider}
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={item.enabled}
                  onChange={() => toggleEnabled(item)}
                />
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              {fieldsCell(item)}
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
              >
                {scopeSummary(item.scope).map((part) => (
                  <Chip
                    key={part}
                    size="small"
                    variant="outlined"
                    label={part}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                <Button size="small" onClick={() => openEdit(item)}>
                  Изменить
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setConfirmDelete(item)}
                >
                  Удалить
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      );
    }

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "16px" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.table.head }}>
              <TableCell sx={{ fontWeight: 700 }}>Набор</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Провайдер</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Поля</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Область действия</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Активен</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{item.label}</TableCell>
                <TableCell>{item.provider}</TableCell>
                <TableCell>{fieldsCell(item)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {scopeSummary(item.scope).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        без ограничений
                      </Typography>
                    ) : (
                      scopeSummary(item.scope).map((part) => (
                        <Chip
                          key={part}
                          size="small"
                          variant="outlined"
                          label={part}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      ))
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={item.enabled}
                    onChange={() => toggleEnabled(item)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Изменить">
                    <IconButton size="small" onClick={() => openEdit(item)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(item)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
        // Тот же боковой отступ, что у остальных разделов панели.
        p: { xs: 2, sm: 3 },
      }}
    >
      <PageHeader
        icon={VpnKeyOutlined}
        title="Ключи провайдеров"
        description="Наборы ключей и область, по которой они подбираются под заказ"
        actions={
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={openCreate}
          >
            Добавить
          </Button>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Select
          size="small"
          displayEmpty
          value={providerFilter}
          onChange={(event) => setProviderFilter(event.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 240 } }}
        >
          <MenuItem value="">Все провайдеры</MenuItem>
          {providers.map((provider: any) => (
            <MenuItem key={provider.id} value={String(provider.id)}>
              {provider.name}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2" color="text.secondary">
          Значения ключей не отдаются: экран показывает только имена полей.
        </Typography>
      </Stack>

      {renderContent()}

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? "Изменение набора" : "Новый набор ключей"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={form.provider_id}
              disabled={Boolean(editing)}
              onChange={(e) => {
                // Прежняя ошибка относилась к прежнему провайдеру.
                setFormError(null);
                // У другого провайдера другие счета и другие имена полей —
                // введённое к ним не относится.
                setForm((prev) => ({
                  ...prev,
                  provider_id: e.target.value,
                  account: "",
                  values: {},
                  payload: "",
                }));
              }}
            >
              <MenuItem value="">Провайдер</MenuItem>
              {providers.map((provider: any) => (
                <MenuItem key={provider.id} value={String(provider.id)}>
                  {provider.name}
                  {schemas[provider.id]?.reads_sets === false
                    ? " — наборы не читает"
                    : ""}
                </MenuItem>
              ))}
            </Select>

            <TextField
              label="Название"
              size="small"
              fullWidth
              value={form.label}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, label: e.target.value }))
              }
              helperText="Показывается в списке — пусть говорит, для чего набор"
            />

            <Alert severity="warning" sx={{ borderRadius: "12px" }}>
              Набор применяется целиком. Если в нём есть ключ, но нет парного
              секрета, интеграция возьмёт прежнюю пару из окружения — половина
              набора выглядит настроенной, но не работает.
            </Alert>

            {editing && Boolean(schema?.accounts.length) && (
              <Typography variant="caption" color="text.secondary">
                Значения не отдаются никому, поэтому поля пустые. Пусто —
                ключи не меняются; заполнено — набор заменяется целиком.
              </Typography>
            )}

            <CredentialPayloadForm
              schema={schema}
              loading={schemasLoading && Boolean(form.provider_id)}
              accountCode={account?.code ?? ""}
              onAccountChange={chooseAccount}
              values={form.values}
              onValueChange={(name, value) =>
                setForm((prev) => ({
                  ...prev,
                  values: { ...prev.values, [name]: value },
                }))
              }
              editing={Boolean(editing)}
              manual={form.payload}
              onManualChange={(text) =>
                setForm((prev) => ({ ...prev, payload: text }))
              }
            />

            <Divider />
            <Typography variant="caption" color="text.secondary">
              Область действия. Пустое поле — не ограничиваем по этому признаку.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={form.payment_method}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    payment_method: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Любой метод</MenuItem>
                {methodOptions.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={form.operation_type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    operation_type: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Любая операция</MenuItem>
                {OPERATION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={form.currency}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, currency: e.target.value }))
                }
              >
                <MenuItem value="">Любая валюта</MenuItem>
                {Object.keys(currency).map((code) => (
                  <MenuItem key={code} value={code.toLowerCase()}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label="Банк"
                size="small"
                fullWidth
                value={form.bank_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, bank_name: e.target.value }))
                }
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Сумма от"
                size="small"
                type="number"
                fullWidth
                value={form.minsum}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, minsum: e.target.value }))
                }
              />
              <TextField
                label="Сумма до"
                size="small"
                type="number"
                fullWidth
                value={form.maxsum}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, maxsum: e.target.value }))
                }
              />
            </Stack>

            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            // Набор у провайдера, который их не читает, молча ничего не
            // сделает — такую кнопку лучше не давать нажать.
            disabled={saving || schema?.reads_sets === false}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Сохранить"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Удалить набор?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Набор «{confirmDelete?.label}» будет удалён. Восстановить его нельзя
            — значения ключей не отдаются никому, включая нас. Придётся вводить
            заново.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Отмена</Button>
          <Button variant="contained" color="error" onClick={runDelete}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar ref={snackbarRef} />
    </Box>
  );
};

export default ProviderCredentialsPage;
